import type { Photo, PhotoPage, SortOrder, PhotoSection, UploadAccess, UploadJob } from '../shared/photo';
import type { EventProfile } from '../shared/profile';
import type { BackupStatus } from '../shared/backup';
import type { EventStatus } from '../shared/event';

type ApiError = {
  error?: {
    code?: string;
    message?: string;
    bannedUntil?: string;
    serverNow?: string;
    uploadsPaused?: boolean;
  };
};
export class UploadAccessError extends Error {
  constructor(
    public readonly access: UploadAccess,
    message?: string,
  ) {
    super(message);
  }
}
function accessError(error: ApiError['error']) {
  if (!error?.serverNow || !['UPLOAD_BANNED', 'UPLOAD_PAUSED'].includes(error.code ?? '')) return null;
  return new UploadAccessError(
    {
      bannedUntil: error.bannedUntil ?? null,
      serverNow: error.serverNow,
      uploadsPaused: Boolean(error.uploadsPaused) || error.code === 'UPLOAD_PAUSED',
    },
    error.message,
  );
}
async function request<T>(url: string, init?: RequestInit, timeout = 20000): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeout);
  try {
    const response = await fetch(url, { ...init, credentials: 'same-origin', signal: controller.signal });
    const data = (await response.json().catch(() => ({}))) as T & ApiError;
    if (!response.ok)
      throw (
        accessError(data.error) ?? new Error(data.error?.message ?? 'Something went wrong. Please try again.')
      );
    return data;
  } catch (error) {
    if (controller.signal.aborted)
      throw new Error('This is taking too long. Please try again.', { cause: error });
    if (error instanceof TypeError)
      throw new Error('Couldn’t connect. Check your connection and try again.', { cause: error });
    throw error;
  } finally {
    clearTimeout(timer);
  }
}

async function waitForUpload(job: UploadJob): Promise<{ photo: Photo }> {
  let current = job;
  let connectionFailures = 0;
  while (current.status !== 'completed') {
    await new Promise((resolve) => setTimeout(resolve, 2000 + Math.random() * 1000));
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch('/api/uploads/' + current.id, {
        credentials: 'same-origin',
        signal: controller.signal,
      });
      const data = (await response.json().catch(() => ({}))) as { upload?: UploadJob } & ApiError;
      if (!response.ok) {
        if (response.status >= 500 && ++connectionFailures < 5) continue;
        throw (
          accessError(data.error) ??
          new Error(data.error?.message ?? 'Your photo could not be saved. Please retry.')
        );
      }
      if (!data.upload || !['queued', 'processing', 'completed'].includes(data.upload.status))
        throw new Error('The server could not confirm your photo. Please retry.');
      current = data.upload;
      connectionFailures = 0;
    } catch (error) {
      // Temporary loss of mobile data must not submit the photo a second time.
      if ((error instanceof TypeError || controller.signal.aborted) && ++connectionFailures < 5) continue;
      throw error;
    } finally {
      clearTimeout(timer);
    }
  }
  if (!current.photo) throw new Error('The server could not confirm your photo. Please retry.');
  return { photo: current.photo };
}

export const api = {
  eventStatus: () => request<EventStatus>('/api/event'),
  setEventLive: (live: boolean, csrf: string) =>
    request<EventStatus>('/api/admin/event', {
      method: 'PUT',
      headers: { 'content-type': 'application/json', 'x-csrf-token': csrf },
      body: JSON.stringify({ live }),
    }),
  endEvent: (csrf: string) =>
    request<EventStatus>('/api/admin/event/end', {
      method: 'POST',
      headers: { 'content-type': 'application/json', 'x-csrf-token': csrf },
      body: JSON.stringify({ confirmed: true }),
    }),
  setAlbumDownloads: (allow: boolean, csrf: string) =>
    request<EventStatus>('/api/admin/event/downloads', {
      method: 'PUT',
      headers: { 'content-type': 'application/json', 'x-csrf-token': csrf },
      body: JSON.stringify({ allow }),
    }),
  sendAlbumInvitation: (csrf: string) => request<EventStatus>('/api/admin/event/invite', {
    method: 'POST', headers: { 'x-csrf-token': csrf },
  }),
  pushKey: () => request<{ publicKey: string }>('/api/push/key'),
  subscribePush: (subscription: PushSubscription) => request('/api/push/subscribe', {
    method: 'POST', headers: { 'content-type': 'application/json', 'x-requested-with': 'lloyds-album' },
    body: JSON.stringify(subscription),
  }),
  requestAlbum: (name: string, email: string) =>
    request<{ ok: boolean; canEdit: boolean }>('/api/album-requests', {
      method: 'POST',
      headers: { 'content-type': 'application/json', 'x-requested-with': 'lloyds-album' },
      body: JSON.stringify({ name, email, consent: true }),
    }),
  correctAlbumSignup: (name: string, email: string, previousEmail: string) =>
    request<{ ok: boolean; canEdit: boolean }>('/api/album-requests', {
      method: 'PUT',
      headers: { 'content-type': 'application/json', 'x-requested-with': 'lloyds-album' },
      body: JSON.stringify({ name, email, previousEmail, consent: true }),
    }),
  uploadAccess: () => request<UploadAccess>('/api/upload-access'),
  setUploadsPaused: (paused: boolean, csrf: string) =>
    request<UploadAccess>('/api/admin/uploads', {
      method: 'PUT',
      headers: { 'content-type': 'application/json', 'x-csrf-token': csrf },
      body: JSON.stringify({ paused }),
    }),
  profile: () => request<EventProfile>('/api/profile'),
  saveProfile: (file: File, csrf: string) => {
    const body = new FormData();
    body.append('photo', file);
    return request<EventProfile>(
      '/api/admin/profile',
      { method: 'POST', headers: { 'x-csrf-token': csrf }, body },
      120000,
    );
  },
  resetProfile: (csrf: string) =>
    request<EventProfile>('/api/admin/profile', { method: 'DELETE', headers: { 'x-csrf-token': csrf } }),
  photos: (sort: SortOrder, page: number, section: PhotoSection = 'celebration') =>
    request<PhotoPage>(`/api/photos?sort=${sort}&page=${page}&pageSize=12&section=${section}`),
  config: () => request<{ pollSeconds: number; maxUploadBytes: number }>('/api/config'),
  upload: (
    file: File,
    caption: string,
    key: string,
    progress: (value: number) => void,
    contributorName = '',
    section: PhotoSection = 'memories',
    received?: () => void,
    bulkCsrf?: string,
  ) =>
    new Promise<{ photo: Photo }>((resolve, reject) => {
      const form = new FormData();
      form.append('caption', caption);
      form.append('contributorName', contributorName);
      form.append('section', section);
      form.append('capturedAt', new Date(file.lastModified || Date.now()).toISOString());
      form.append('photo', file);
      const xhr = new XMLHttpRequest();
      xhr.open('POST', bulkCsrf ? '/api/admin/bulk-photos' : '/api/photos');
      if (bulkCsrf) xhr.setRequestHeader('x-csrf-token', bulkCsrf);
      xhr.timeout = 120000;
      xhr.setRequestHeader('idempotency-key', key);
      xhr.setRequestHeader('prefer', 'respond-async');
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) progress(Math.round((event.loaded / event.total) * 100));
      };
      xhr.onerror = () =>
        reject(new Error('Connection lost. Your photo is still here. Reconnect and retry.'));
      xhr.ontimeout = () =>
        reject(new Error('The upload took too long. Retry safely; your photo won’t be added twice.'));
      xhr.onload = () => {
        let data: { photo: Photo; upload?: UploadJob } & ApiError;
        try {
          data = JSON.parse(xhr.responseText);
        } catch {
          reject(new Error('The server couldn’t confirm your photo. Please retry.'));
          return;
        }
        if (xhr.status === 202 && data.upload) {
          received?.();
          void waitForUpload(data.upload).then(resolve, reject);
        } else if (xhr.status >= 200 && xhr.status < 300) resolve(data);
        else
          reject(
            accessError(data.error) ??
              new Error(data.error?.message || 'Your photo couldn’t be published. Please retry.'),
          );
      };
      xhr.send(form);
    }),
  session: () => request<{ authenticated: boolean; csrfToken: string | null }>('/api/admin/session'),
  login: (password: string) =>
    request<{ authenticated: boolean; csrfToken: string }>('/api/admin/login', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ password }),
    }),
  logout: (csrf: string) =>
    request('/api/admin/logout', { method: 'POST', headers: { 'x-csrf-token': csrf } }),
  remove: (id: string, csrf: string) =>
    request(`/api/admin/photos/${id}`, { method: 'DELETE', headers: { 'x-csrf-token': csrf } }),
  removeOwn: (id: string) =>
    request(`/api/photos/${id}`, { method: 'DELETE', headers: { 'x-requested-with': 'lloyds-album' } }),
  ban: (id: string, csrf: string) =>
    request(`/api/admin/photos/${id}/ban`, { method: 'POST', headers: { 'x-csrf-token': csrf } }),
  restore: (id: string, csrf: string) =>
    request(`/api/admin/photos/${id}/restore`, { method: 'POST', headers: { 'x-csrf-token': csrf } }),
  trash: () => request<PhotoPage>('/api/admin/trash'),
  backupStatus: () => request<BackupStatus>('/api/admin/backups'),
  backupSetup: (csrf: string) =>
    request<{ script: string; manifest: string }>('/api/admin/backups/setup', {
      method: 'POST',
      headers: { 'x-csrf-token': csrf },
    }),
  purge: (ids: string[], csrf: string) =>
    request<{ deletedIds: string[] }>('/api/admin/trash', {
      method: 'DELETE',
      headers: { 'content-type': 'application/json', 'x-csrf-token': csrf },
      body: JSON.stringify({ ids }),
    }),
};
